<template>
  <div class="member-level-field">
    <BaseElFormItem :label="$t('member.tab.memberLevelField.enableLevel')">
      <BaseElSwitch
        v-model="enableLevel"
        :inactive-text="$t('member.tab.memberLevelField.levelOff')"
        :active-text="$t('member.tab.memberLevelField.levelOn')"
        @change="onEnableLevel"
      />

      <div class="field-area">
        <BaseElFormItem :label="$t('member.tab.memberLevelField.memberLevel')" class="flex-1" prop="level">
          <BaseElSelect
            v-model="syncFormData.level"
            :disabled="isDisabled"
            @change="onLevelChange"
          >
            <BaseElSelectOption
              v-for="level in levels"
              :key="level.level"
              :label="levelOptionLabel(level)"
              :value="level.level"
            />
          </BaseElSelect>
        </BaseElFormItem>
        <BaseElFormItem :label="$t('member.tab.memberLevelField.recognizedStartDate')" class="flex-1" prop="recognizedTimeReset">
          <BaseElSelect
            v-model="syncFormData.recognizedTimeReset"
            :disabled="isDisabled"
            :placeholder="$t('member.tab.memberLevelField.pleaseSelect')"
          >
            <BaseElSelectOption
              v-for="option in recognizedStartDateOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </BaseElSelect>
        </BaseElFormItem>
        <BaseElFormItem
          v-if="syncFormData.recognizedTimeReset === 'fromDaysBeforeUpgrade'"
          :label="$t('member.tab.memberLevelField.forwardDays')"
          class="flex-1"
          prop="recognizedTimeResetValue"
        >
          <div class="inline-flex items-center gap-2">
            <span>{{ $t('member.tab.memberLevelField.forwardDays') }}</span>
            <BaseElInput
              v-model.number="syncFormData.recognizedTimeResetValue"
              :disabled="isDisabled"
              style="width: 80px"
              type="number"
              :min="1"
              :placeholder="$t('member.tab.memberLevelField.pleaseEnter')"
            />
            <span>{{ $t('member.tab.memberLevelField.daysBeforeLevelChange') }}</span>
          </div>
        </BaseElFormItem>
        <BaseElFormItem class="flex-1" prop="levelExpiredAt">
          <p class="text-[16px] text-[#333] font-medium">
            <span class="text-danger">* </span>
            <span class="pr-2">{{ $t('member.tab.memberLevelField.memberExpiry') }}</span>
            <span
              v-if="syncFormData.level !== 1 && enableLevel && levelConfig.levelRemainCondition !== 'none'"
              class="inline-flex items-center"
              @click="showLevelRemainDialog = true"
            >
              <img
                v-if="syncFormData.isLevelRemainFulfilled"
                src="@/assets/member/fill_check.svg"
                class="cursor-pointer"
              >
              <span
                class="text-xs cursor-pointer"
                :class="
                  syncFormData.isLevelRemainFulfilled
                    ? 'text-[#00CD20]'
                    : 'text-[#767676]'
                "
              >{{
                syncFormData.isLevelRemainFulfilled
                  ? $t('member.tab.memberLevelField.renewalQualified')
                  : $t('member.tab.memberLevelField.renewalNotQualified')
              }}</span>
            </span>
          </p>
          <div class="flex flex-col">
            <el-date-picker
              v-if="!formData.allRange"
              v-model="syncFormData.levelExpiredAt"
              editable
              type="date"
              format="yyyy/MM/dd"
              :picker-options="pickerOption"
              :disabled="isDisabled || syncFormData.forever"
            />
            <BaseElCheckbox
              v-model="syncFormData.forever"
              :disabled="isDisabled"
              @change="onForeverChange"
            >
              {{ $t('member.tab.memberLevelField.permanent') }}
            </BaseElCheckbox>
          </div>
        </BaseElFormItem>
        <BaseElFormItem>
          <template slot="label">
            <div class="flex items-center gap-[8px]">
              <p>{{ $t('member.tab.memberLevelField.storeNote') }}</p>
              <p class="text-sm text-gray-80">
                {{ $t('member.tab.memberLevelField.storeNoteHint') }}
              </p>
              <Tag v-if="isSystemNote" size="small">
                {{ $t('member.tab.memberLevelField.systemGeneratedNote') }}
              </Tag>
            </div>
          </template>
          <BaseElInput
            v-model="syncFormData.levelNote"
            :disabled="isDisabled"
            class="levelNote"
            :class="{ 'is-system-note': isSystemNote }"
            type="textarea"
            :autosize="{ minRows: 8, maxRows: 8 }"
            :placeholder="$t('member.tab.memberLevelField.pleaseEnter')"
          />
        </BaseElFormItem>
      </div>
    </BaseElFormItem>
    <LevelRemainDialog
      :show.sync="showLevelRemainDialog"
      :isLevelRemainFulfilled.sync="syncFormData.isLevelRemainFulfilled"
      :levelConfig="levelConfig"
    />
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from 'vue'
import { useVModel } from '@vueuse/core'
import { MemberLevelExpiredAt, FindMemberLevelRecord } from '@/api/memberLevel'
import { find, filter, minBy, get } from 'lodash'
import { useFetch } from '@/use/fetch'
import { useShop } from '@/use/shop'
import { useOrgBenefitControl } from '@/use/useOrgBenefitControl'
import LevelRemainDialog from './LevelRemainDialog.vue'
import Tag from '@/components/Tag/Tag.vue'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'MemberLevelField',
  components: { LevelRemainDialog, Tag },
  props: {
    formData: { type: Object, default: () => ({}) },
    member: { type: Object, default: () => ({}) },
  },
  setup (props, { emit }) {
    const syncFormData = useVModel(props, 'formData', emit)
    const { simpleFetch, fetchAll } = useFetch()
    const { shopId, shopMemberLevelConfig } = useShop()
    const { isMemberLevelReadOnly } = useOrgBenefitControl()
    const enableLevel = ref(false)
    const isDisabled = computed(() => !enableLevel.value)

    // CBGISSUE-266：levelNote 為純文字欄位無來源標記（手動也可輸入相同文字，不做文字比對），
    // 權威判定查 MemberLevelRecord：source 為系統值且無操作者（User.id 為 null）→ 系統產生
    const SYSTEM_NOTE_SOURCES = ['systemUpgrade', 'systemDowngrade', 'systemRemain', 'eventPlaybook', 'voidRecalculation']

    const levelRecords = ref([])
    const isSystemNote = computed(() => {
      const note = syncFormData.value.levelNote || ''
      if (!note) return false
      const valid = filter(levelRecords.value, (r) => !r.isVoided && (r.note || '') === note)
      if (!valid.length) return false
      // 取最舊一筆（原始寫入者）判定：表單預填舊值未修改就調整等級時，系統文字會原樣寫入新紀錄（drift）；
      // 清空則送 levelNote: ''，真的清除、不沿用（僅呼叫端未帶 levelNote 才會沿用舊值）
      const origin = minBy(valid, 'createdAt')
      return SYSTEM_NOTE_SOURCES.includes(origin.source) && !get(origin, 'User.id')
    })

    const currentLevel = ref(syncFormData.value.level)
    const originExpiredDate = ref(null)

    const showLevelRemainDialog = ref(false)

    const levels = computed(() => filter(shopMemberLevelConfig.value, c => c.level !== 0))
    const levelsExpiredAt = ref([])
    const levelConfig = computed(() => {
      const config = find(levels.value, { level: syncFormData.value.level }) || {}
      const levelsExpiredAtConfig = find(levelsExpiredAt.value, { level: syncFormData.value.level }) || {}
      return {
        ...config,
        levelsExpiredAt: levelsExpiredAtConfig.expiredAt,
      }
    })

    const pickerOption = {
      disabledDate (time) {
        if (!originExpiredDate.value) return false
        return new Date(time.getTime()) < originExpiredDate.value
      },
    }

    const onForeverChange = (forever) => {
      if (forever) {
        syncFormData.value.levelExpiredAt = null
      }
      if (!forever) {
        if (props.member.levelExpiredAt) syncFormData.value.levelExpiredAt = props.member.levelExpiredAt
      }
    }

    const onEnableLevel = (enable) => {
      if (!enable) {
        syncFormData.value.level = null
        syncFormData.value.levelExpiredAt = null
        syncFormData.value.levelNote = null
        syncFormData.value.forever = true
      } else {
        if (!currentLevel.value) syncFormData.value.level = 1
        else syncFormData.value.level = currentLevel.value

        const levelNote = props.member.levelNote
        const levelExpiredAt = props.member.levelExpiredAt

        if (levelNote) syncFormData.value.levelNote = levelNote
        if (levelExpiredAt) {
          syncFormData.value.levelExpiredAt = levelExpiredAt
          syncFormData.value.forever = false
        }
      }
    }

    const onLevelChange = (level) => {
      const levelExpiredAt = find(levelsExpiredAt.value, { level }) || {}
      if (levelExpiredAt.expiredAt) {
        syncFormData.value.forever = false
        syncFormData.value.levelExpiredAt = levelExpiredAt.expiredAt
      } else {
        syncFormData.value.forever = true
        syncFormData.value.levelExpiredAt = null
      }
    }

    onMounted(async () => {
      originExpiredDate.value = new Date(props.member.levelExpiredAt)
      simpleFetch(MemberLevelExpiredAt, { shopId: shopId.value }, (res) => {
        levelsExpiredAt.value = res
      })
      if (props.member.id) {
        fetchAll(FindMemberLevelRecord, { shopId: shopId.value, id: props.member.id }, (res) => {
          levelRecords.value = res || []
        })
      }
      if (props.formData.level) {
        enableLevel.value = true
      }
      if (!props.formData.level) {
        enableLevel.value = false
        syncFormData.value.level = null
      }
    })

    const recognizedStartDateOptions = [
      { label: t('member.tab.memberLevelField.noChange'), value: 'noChange' },
      { label: t('member.tab.memberLevelField.fromUpgradeDay'), value: 'fromUpgradeDay' },
      { label: t('member.tab.memberLevelField.fromDaysBefore'), value: 'fromDaysBefore' },
    ]

    const levelOptionLabel = (level) => {
      if (isMemberLevelReadOnly.value && level.Shop?.name) {
        return `${level.name}（${t('member.tab.memberInfo.issuingStore')}：${level.Shop.name}）`
      }
      return level.name
    }

    return {
      syncFormData,
      isDisabled,
      isSystemNote,
      levels,
      levelsExpiredAt,
      onForeverChange,
      enableLevel,
      onEnableLevel,
      showLevelRemainDialog,
      onLevelChange,
      pickerOption,
      currentLevel,
      levelConfig,
      recognizedStartDateOptions,
      levelOptionLabel,
      isMemberLevelReadOnly,
    }
  },
})
</script>

<style lang="postcss" scoped>
.field-area {
  @apply p-[20px] bg-[#F5F7FA];
}

.levelNote.is-system-note :deep(.el-textarea__inner) {
  @apply text-gray-50;
}

::v-deep .comment.el-textarea {
  @apply w-full;
}
::v-deep .el-input {
  @apply w-full;
}
::v-deep .el-select {
  @apply w-full;
}
</style>
